import React, { useState, useEffect } from "react";
import "../assets/css/Contact.css";
import ReCAPTCHA from "react-google-recaptcha";
import { IoMdMail } from "react-icons/io";
import toast, { Toaster } from "react-hot-toast";

// Bypass reCAPTCHA for development/testing
// To enable bypass, set environment variable: REACT_APP_SKIP_RECAPTCHA=true
const isDevelopment =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_SKIP_RECAPTCHA === "true";

// Function to format validation error message for specific fields
const getFieldError = (fieldName, errors) => {
  if (!errors || !Array.isArray(errors)) return null;
  const error = errors.find((e) => e.field === fieldName);
  return error ? error.message : null;
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    number: "",
    email: "",
    company: "",
    looking: "",
    message: "",
  });

  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [fieldErrors, setFieldErrors] = useState(null);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCaptcha = (value) => {
    if (!isDevelopment) {
      setCaptchaToken(value);
      setCaptchaError(""); // clear error
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setCaptchaError("Captcha expired. Please complete it again.");
  };

  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setCaptchaError("Captcha failed to load. Please refresh the page.");
    // Auto-refresh after error
    setTimeout(() => {
      setCaptchaError("");
      setCaptchaLoaded(false);
    }, 2000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "number") {
      const cleanedValue = value.replace(/\D/g, "");

      setFormData({
        ...formData,
        [name]: cleanedValue,
      });

      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate captcha
    if (!isDevelopment && !captchaToken) {
      toast.error("Please complete the captcha verification");
      setCaptchaError("Please complete the captcha verification");
      return;
    }

    setIsSubmitting(true);
    setFieldErrors(null);

    // Prepare payload
    const payload = {
      last_name: formData.name,
      phone: formData.number,
      email: formData.email,
      company: formData.company,
      looking_for: formData.looking,
      message: formData.message,
      token: captchaToken
    };

    try {
      const response = await fetch(
        "https://innothrust.com/api/create_lead",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (response.ok) {
        toast.success("Form Submitted Successfully");

        console.log("Success:", data);

        // Reset form
        setFormData({
          name: "",
          number: "",
          email: "",
          company: "",
          looking: "",
          message: "",
        });
        setFieldErrors(null);
        setCaptchaToken(null);
        setCaptchaLoaded(false);
      } else {
        console.error("API Error:", data);

        // Handle validation errors from the API response
        if (data.detail && Array.isArray(data.detail)) {
          setFieldErrors(data.detail);
          const errorMessages = data.detail.map(
            (error) => `${error.field}: ${error.message}`,
          );
          toast.error(errorMessages.join(" | "));
        } else {
          toast.error(data.message || "Something went wrong");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Server error. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            style: {
              background: "#4CAF50",
            },
          },
          error: {
            style: {
              background: "#F44336",
            },
          },
        }}
      />
      <section className="contact-section">
        <div className="container">
          <div className="row">
            {/* Left */}
            <div className="col-lg-5 mb-5">
              <h2 className="contact-heading">Let’s work together</h2>

              <h5 className="mb-4">
                Get in touch today and receive a consultation.
              </h5>

              <h3>Email</h3>
              <h6>For General Enquiry</h6>

              <p>
                <IoMdMail className="me-2" />

                <a href="mailto:info@innothrust.com" className="contact-email">
                  info@innothrust.com
                </a>
              </p>

              <h6>For Job Seekers</h6>

              <p>
                <IoMdMail className="me-2" />

                <a href="mailto:hr@innothrust.com" className="contact-email">
                  hr@innothrust.com
                </a>
              </p>
            </div>

            {/* Right */}
            <div className="col-lg-7">
              <div className="contact-form-box">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <input
                        type="text"
                        className={`form-control ${fieldErrors?.some((e) => e.field === "name") ? "is-invalid" : ""}`}
                        placeholder="Name *"
                        name="name"
                        onChange={handleChange}
                        required
                      />
                      {fieldErrors?.find((e) => e.field === "name") && (
                        <div className="invalid-feedback">
                          {getFieldError("name", fieldErrors)}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={15}
                        className={`form-control ${fieldErrors?.some((e) => e.field === "phone") ? "is-invalid" : ""}`}
                        placeholder="Mobile Number *"
                        name="number"
                        value={formData.number}
                        onChange={handleChange}
                        required
                      />
                      {fieldErrors?.find((e) => e.field === "phone") && (
                        <div className="invalid-feedback">
                          {getFieldError("phone", fieldErrors)}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <input
                        type="email"
                        className={`form-control ${fieldErrors?.some((e) => e.field === "email") ? "is-invalid" : ""}`}
                        placeholder="Email *"
                        name="email"
                        onChange={handleChange}
                        required
                      />
                      {fieldErrors?.find((e) => e.field === "email") && (
                        <div className="invalid-feedback">
                          {getFieldError("email", fieldErrors)}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <input
                        type="text"
                        className={`form-control ${fieldErrors?.some((e) => e.field === "company") ? "is-invalid" : ""}`}
                        placeholder="Company"
                        name="company"
                        onChange={handleChange}
                      />
                      {fieldErrors?.find((e) => e.field === "company") && (
                        <div className="invalid-feedback">
                          {getFieldError("company", fieldErrors)}
                        </div>
                      )}
                    </div>

                    <div className="col-md-12 mb-3">
                      <span>Looking for</span>
                      <select
                        className={`form-select ${fieldErrors?.some((e) => e.field === "looking_for") ? "is-invalid" : ""}`}
                        name="looking"
                        value={formData.looking}
                        onChange={handleChange}
                        required
                        aria-invalid={fieldErrors?.some(
                          (e) => e.field === "looking_for",
                        )}
                      >
                        <option value="">Select</option>

                        <option value="General">General</option>

                        <option value="Vendor Services">Vendor Services</option>

                        <option value="Business Enquiry">
                          Business Enquiry
                        </option>

                        <option value="Media">Media</option>
                      </select>
                      {fieldErrors?.find((e) => e.field === "looking_for") && (
                        <div className="invalid-feedback">
                          {getFieldError("looking_for", fieldErrors)}
                        </div>
                      )}
                    </div>

                    <div className="col-md-12 mb-3">
                      <textarea
                        className={`form-control ${fieldErrors?.some((e) => e.field === "message") ? "is-invalid" : ""}`}
                        rows="6"
                        value={formData.message}
                        placeholder="Message *"
                        name="message"
                        onChange={handleChange}
                        required
                      ></textarea>
                      {fieldErrors?.find((e) => e.field === "message") && (
                        <div className="invalid-feedback">
                          {getFieldError("message", fieldErrors)}
                        </div>
                      )}
                    </div>

                    <div className="mt-3 mb-3">
                      {isDevelopment ? (
                        <div className="alert alert-info mb-0">
                          <small>Development mode: reCAPTCHA bypassed</small>
                        </div>
                      ) : (
                        <>
                          <ReCAPTCHA
                            sitekey={
                              process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                              "6Ldoo2YsAAAAADbN-9P-bnHPGjMLgid2nCnpBd7m"
                            }
                            onChange={handleCaptcha}
                            onExpired={handleCaptchaExpired}
                            onError={handleCaptchaError}
                            hl="en"
                          />

                          {captchaError && (
                            <div className="fd-error-message mt-2">
                              {captchaError}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="col-md-12">
                      <button
                        type="submit"
                        className=" btn-contact-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Submitting...
                          </>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactForm;
